import React, { useState } from 'react';
import api from 'helpers/api';
import useGym from 'helpers/use_gym';
import notification from 'helpers/notification';
import plural from 'helpers/plural';
import Button from 'react/components/button';
import sources from 'helpers/sources';
import { Contact } from 'react/types/contact';

export type ConversionType =
  | 'member_to_nac'
  | 'prospect_to_nac'
  | 'nac_to_member'
  | 'prospect_to_member'
  | 'nac_to_prospect';

export const getConvertContactModalTitle = (): string => {
  return 'Convert Contact';
};

interface ConvertContactModalProps {
  cancel: () => void;
  submitModal: () => void;
  contact?: Contact;
  contacts?: Contact[];
  conversionType: ConversionType;
}

const ConvertContactModal = ({
  cancel,
  submitModal,
  contact,
  contacts,
  conversionType,
}: ConvertContactModalProps) => {
  const gym = useGym();
  const [isLoading, setIsLoading] = useState(false);

  const isBulk = !!contacts;
  const contactsToConvert = isBulk ? contacts : [contact];
  const count = contactsToConvert.length;

  const getConversionDetails = () => {
    const details: {
      [key in ConversionType]: { url: string; from: string; to: string; warning?: string };
    } = {
      member_to_nac: {
        url: isBulk ? sources.bulkConvertMemberToNac() : sources.convertContactToNac(),
        from: plural('Member', count),
        to: `Non-attending ${plural('contact', count)}`,
        warning: 'This will remove any upcoming bookings for these contacts, if they exist.',
      },
      prospect_to_nac: {
        url: isBulk ? sources.bulkConvertProspectToNac() : sources.convertContactToNac(),
        from: plural('Prospect', count),
        to: `Non-attending ${plural('contact', count)}`,
        warning: 'This will remove any upcoming bookings for these contacts, if they exist.',
      },
      nac_to_member: {
        url: isBulk ? sources.bulkConvertNacToMember() : sources.convertNacToMember(),
        from: `Non-attending ${plural('contact', count)}`,
        to: plural('Member', count),
      },
      prospect_to_member: {
        url: isBulk ? sources.bulkConvertProspectToMember() : sources.convertProspectToMember(),
        from: plural('Prospect', count),
        to: plural('Member', count),
      },
      nac_to_prospect: {
        url: isBulk ? sources.bulkConvertNacToProspect() : sources.convertNacToProspect(),
        from: `Non-attending ${plural('contact', count)}`,
        to: plural('Prospect', count),
        warning: 'This will remove any active memberships for these contacts, if they exist.',
      },
    };
    return details[conversionType];
  };

  const submit = () => {
    setIsLoading(true);
    const { url, from, to } = getConversionDetails();

    const apiCall = isBulk
      ? api.put(url, { ids: contactsToConvert.map((c) => c.id) })
      : api.put(url, { id: contact.id });

    apiCall.then(
      () => {
        const message = `${count} ${from} converted to ${to}`;
        notification.success(message);
        submitModal();
      },
      (error) => {
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.error_message ||
          'Request could not be completed. Please try again';
        notification.error(errorMessage);
        setIsLoading(false);
      },
    );
  };

  const { from, to, warning } = getConversionDetails();

  return (
    <>
      <div className="modal-body">
        <p>
          Convert {count} selected {from} to {to}?
        </p>
        {warning && <p className="text-warning">{warning}</p>}
      </div>
      <div className="modal-footer">
        <Button onClick={() => cancel()} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="success" onClick={() => submit()} disabled={isLoading}>
          Convert
        </Button>
      </div>
    </>
  );
};

export default ConvertContactModal;
